import type { AxiosInstance } from 'axios'
import type { IFactoryFunctionRequest, IFactoryResponse } from '@bc-library/interface'
import { EMethod } from '@bc-library/interface'
import useAxios from '@/services/api/useAxios'
import { API_MAIN_VERSION, BASE_URL } from '@/config/constantes'

const resource = 'outlook'

export type TOutlookConnectionStatus = {
  connected: boolean
  configured?: boolean
  email?: string | null
  connectedAt?: string | Date | null
  updatedAt?: string | Date | null
  microsoftTenantId?: string | null
  hideEventTitles?: boolean
  // Connected with the consent to organize Teams meetings (older connections must reconnect)
  canCreateTeamsMeetings?: boolean
  error?: boolean
}

export type TOutlookBusyEvent = {
  id: string
  type: 'outlook'
  title: string | null
  start: string
  end: string
  resourceIds: string[]
  showAs?: string
}

const getMyStatus = (axios: IFactoryFunctionRequest<TOutlookConnectionStatus, void>) => (): IFactoryResponse<TOutlookConnectionStatus, void> => {
  return axios({
    method: EMethod.get,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/me`,
  })
}

const disconnectMe = (axios: IFactoryFunctionRequest<TOutlookConnectionStatus, void>) => (): IFactoryResponse<TOutlookConnectionStatus, void> => {
  return axios({
    method: EMethod.delete,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/me`,
  })
}

const updateMyPreferences = (axios: IFactoryFunctionRequest<TOutlookConnectionStatus, { hideEventTitles: boolean }>) => (
  hideEventTitles: boolean,
): IFactoryResponse<TOutlookConnectionStatus, { hideEventTitles: boolean }> => {
  return axios({
    method: EMethod.patch,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/me`,
    data: { hideEventTitles },
  })
}

const getConnections = (axios: IFactoryFunctionRequest<{ connections: Record<string, TOutlookConnectionStatus>; configured: boolean }, { resourceIds: string[] }>) => (
  resourceIds: string[],
): IFactoryResponse<{ connections: Record<string, TOutlookConnectionStatus>; configured: boolean }, { resourceIds: string[] }> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/connections`,
    data: { resourceIds },
  })
}

const getAvailability = (axios: IFactoryFunctionRequest<{
  events: TOutlookBusyEvent[]
  connections: Record<string, TOutlookConnectionStatus>
}, {
  resourceIds: string[]
  startDate: string | Date
  endDate: string | Date
}>) => (
  payload: { resourceIds: string[]; startDate: string | Date; endDate: string | Date },
): IFactoryResponse<{
  events: TOutlookBusyEvent[]
  connections: Record<string, TOutlookConnectionStatus>
}, {
  resourceIds: string[]
  startDate: string | Date
  endDate: string | Date
}> => {
  return axios({
    method: EMethod.post,
    url: `${BASE_URL}/${API_MAIN_VERSION}/${resource}/availability`,
    data: payload,
  })
}

export default (axios: AxiosInstance) => ({
  getMyStatus: getMyStatus(useAxios(axios)),
  disconnectMe: disconnectMe(useAxios(axios)),
  updateMyPreferences: updateMyPreferences(useAxios(axios)),
  getConnections: getConnections(useAxios(axios)),
  getAvailability: getAvailability(useAxios(axios)),
})
